/**
 * "My enrollment cert" — the transparency surface from plan/security.md §6.1.
 *
 * Fetches the accountant's own cert blob, runs the same `verifyEnrollmentCert`
 * the client uploader runs (against the bundled SR_pk), and renders the
 * verification result. Lets the accountant visually confirm: yes, my cert
 * is signed by the operator, here's the fingerprint + expiry.
 *
 * Not a security boundary — the dashboard already holds its own O_pk/O_sk
 * locally, so cert verification here doesn't gate decryption. This is
 * pure UX: "the system is genuinely operator-signed and I can see it."
 * Verification on UPLOAD is in apps/client-web (the load-bearing path).
 */

import { Alert, Badge, Card, Code, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";

import {
  CertVerifyError,
  fingerprintCertBlob,
  isPlaceholder,
  verifyEnrollmentCert,
  type EnrollmentCert,
} from "@dovach/shared-crypto";

import { api } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "no_cert" }
  | { kind: "verified"; cert: EnrollmentCert; fingerprint: string; blob: string }
  | { kind: "failed"; reason: string; blob: string };

export function CertViewScreen() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const placeholder = isPlaceholder();

  useEffect(() => {
    (async () => {
      try {
        const row = await api.keypair();
        if (!row.cert_blob) {
          setState({ kind: "no_cert" });
          return;
        }
        try {
          const cert = await verifyEnrollmentCert(row.cert_blob);
          setState({
            kind: "verified",
            cert,
            fingerprint: fingerprintCertBlob(row.cert_blob),
            blob: row.cert_blob,
          });
        } catch (err) {
          const reason =
            err instanceof CertVerifyError
              ? `${err.reason}: ${err.message}`
              : (err as Error).message ?? "unknown error";
          setState({ kind: "failed", reason, blob: row.cert_blob });
        }
      } catch (err) {
        setState({
          kind: "failed",
          reason: (err as Error).message ?? "fetch failed",
          blob: "",
        });
      }
    })();
  }, []);

  return (
    <Stack maw={720}>
      <Title order={1}>תעודת הזיהוי שלי</Title>
      <Text c="dimmed">
        כאן את/ה רואה/ה את תעודת ההצפנה המקושרת לחשבון שלך. כשלקוח מעלה קבצים, הדפדפן
        שלו מאמת מקומית שתעודה זו חתומה על-ידי מפעיל המערכת — זהו ההגנה מפני
        החלפת-תעודה (host-substitution). העמוד הזה מציג את אותה אימות מצד הדשבורד
        לשקיפות.
      </Text>

      {placeholder && (
        <Alert color="yellow">
          <Text fw={500}>בילד פיתוח</Text>
          המפתח הציבורי של מפעיל המערכת (SR_pk) הוא PLACEHOLDER בבילד הזה. בילד
          ייצור הקישור הזה יוחלף ב-SR_pk אמיתי שהמפעיל ייצר ב-<Code>dovach-op init</Code>.
        </Alert>
      )}

      {state.kind === "loading" && (
        <Group>
          <Loader size="sm" />
          <Text>טוען תעודה ומאמת...</Text>
        </Group>
      )}

      {state.kind === "no_cert" && (
        <Card withBorder padding="lg">
          <Stack>
            <Badge color="gray" variant="light" size="lg">
              אין תעודה עדיין
            </Badge>
            <Text>
              עדיין לא נחתמה לך תעודה. השלם/י קודם את אשף הגדרת מפתח ההצפנה
              ופנה/י למפעיל לחתימה.
            </Text>
          </Stack>
        </Card>
      )}

      {state.kind === "verified" && (
        <Card withBorder padding="lg">
          <Stack>
            <Group justify="space-between">
              <Title order={3}>פרטי התעודה</Title>
              <Badge color="green" size="lg" variant="filled">
                ✓ תעודה תקפה
              </Badge>
            </Group>
            <Field label="גרסה" value={`v=${state.cert.v}`} mono />
            <Field
              label="מזהה חשבון (קשור לתעודה)"
              value={state.cert.account_id}
              mono
            />
            <Field
              label="מפתח ציבורי (O_pk)"
              value={state.cert.office_pk}
              mono
              wrap
            />
            <Field label="טביעת אצבע (BLAKE2b-160)" value={state.fingerprint} mono wrap />
            <Field
              label="נחתם בתאריך"
              value={formatDate(state.cert.issued_at)}
            />
            <Field
              label="תקף עד"
              value={`${formatDate(state.cert.not_after)} (${daysRemaining(state.cert.not_after)} ימים)`}
            />
            <Text size="xs" c="dimmed">
              חתימת המפעיל מאומתת מקומית מול ה-SR_pk שמובא בבילד של הדשבורד —
              ללא תקשורת רשת לאימות. אותו אימות בדיוק רץ אצל הלקוח לפני
              ההעלאה.
            </Text>
          </Stack>
        </Card>
      )}

      {state.kind === "failed" && (
        <Alert color="red" title="אימות התעודה נכשל">
          <Stack gap={6}>
            <Text>סיבה: <Code>{state.reason}</Code></Text>
            <Text size="sm">
              משמעות התקלה: לקוחות לא יוכלו להעלות אליך קבצים עד שהתעודה תוחלף
              בתעודה חוקית. פנה/י למפעיל המערכת.
            </Text>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

function Field({
  label,
  value,
  mono,
  wrap,
}: {
  label: string;
  value: string;
  mono?: boolean;
  wrap?: boolean;
}) {
  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <Text fw={500} miw={180}>{label}</Text>
      {mono ? (
        <Code style={{ wordBreak: wrap ? "break-all" : "normal" }}>{value}</Code>
      ) : (
        <Text>{value}</Text>
      )}
    </Group>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("he-IL", {
      timeZone: "Asia/Jerusalem",
      dateStyle: "long",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function daysRemaining(notAfterIso: string): number {
  const now = Date.now();
  const exp = new Date(notAfterIso).getTime();
  return Math.max(0, Math.floor((exp - now) / (1000 * 60 * 60 * 24)));
}
